import {
  getExtensionId,
  getCookies,
  createWindow,
  waitOnRemoveWindow,
  removeWindow,
  webRequest,
  checkExtensionVersion,
  type Cookie,
} from ".";

class NotLoggedInError extends Error {
  response: any;

  constructor(response) {
    super(
      "There was an error, please log out from Whatnot and log into it again."
    );
    this.response = response;
  }
}

export async function getWhatnotCookies(
  extensionId: string
): Promise<Cookie[]> {
  return await getCookies(extensionId, {
    domain: ".whatnot.com",
  });
}

export const RequiredExtensionVersion = "1.0.3";

export function checkWhatnotRequiredExtensionVersion() {
  return checkExtensionVersion(RequiredExtensionVersion);
}

function cookiesForStorage(cookies: Cookie[]): Cookie[] {
  return cookies.filter((c) => c.name && c.value);
}

function isLoggedInWhatnot(cookies: Cookie[]): boolean {
  const refreshToken = cookies.find(
    (c) => c.name === "__Secure-refresh-token"
  )?.value;

  return !!refreshToken;
}

async function performWhatnotLogin(
  extensionId: string
): Promise<Cookie[] | null> {
  const windowsCreateResponse = await createWindow(extensionId, {
    focused: true,
    type: "popup",
    url: "https://www.whatnot.com/login/",
  });

  const windowId = windowsCreateResponse.result;

  let closed = false;

  await Promise.any([
    waitOnRemoveWindow(extensionId, {
      windowId,
    }),
    new Promise<void>((rs) => {
      const check = async () => {
        if (closed) return rs();
        const cookies = await getWhatnotCookies(extensionId);

        if (isLoggedInWhatnot(cookies)) {
          try {
            if (!!(await fetchCurrentUser(extensionId))?.username) {
              removeWindow(extensionId, {
                windowId,
              });
              return rs();
            }
          } catch (e) {
            console.warn(
              "User cookies found, but no username in response",
              (e as NotLoggedInError).response
            );
          }
        }

        setTimeout(() => check(), 1000);
      };

      check();
    }),
  ]);
  closed = true;

  const cookies = await getWhatnotCookies(extensionId);
  try {
    const isLoggedIn =
      isLoggedInWhatnot(cookies) &&
      !!(await fetchCurrentUser(extensionId))?.username;
    if (isLoggedIn) return cookiesForStorage(cookies);
  } catch (e) {
    console.warn(
      "User cookies found, but no username in response",
      (e as NotLoggedInError).response
    );
  }

  return null;
}

async function fetchCurrentUser(extensionId: string): Promise<{
  username: string;
  userId: string;
}> {
  const response = await webRequest(extensionId, {
    url: "https://www.whatnot.com/",
    init: {
      method: "GET",
    },
  });
  const data = response?.result?.data ?? "";
  const usernameMatch = data.match(/\\"username\\":\\"([^\\]+)\\"/);
  if (!usernameMatch) {
    throw new NotLoggedInError(response);
  }
  const userIdMatch = data.match(/\\"userID\\":\\"?(.*?)\\"?[,}]/);
  if (!userIdMatch?.[1]) {
    throw new Error(
      "Unable to detect your Whatnot user ID. Please log out of Whatnot and log back in, then try again."
    );
  }
  return {
    username: usernameMatch[1],
    userId: userIdMatch[1],
  };
}

export async function getWhatnotUser(): Promise<{
  username: string;
  userId: string;
  cookies: Cookie[];
}> {
  checkWhatnotRequiredExtensionVersion();

  const extensionId = getExtensionId();

  let cookies = await getWhatnotCookies(extensionId);

  if (!isLoggedInWhatnot(cookies)) {
    await performWhatnotLogin(extensionId);
    cookies = await getWhatnotCookies(extensionId);

    if (!isLoggedInWhatnot(cookies)) {
      throw new Error(
        "Log into Whatnot to allow My Reseller Genie to connect."
      );
    }
  }

  const { username, userId } = await fetchCurrentUser(extensionId);

  return {
    username,
    userId,
    cookies: cookiesForStorage(cookies),
  };
}
